import type {
  DashboardMetricLike,
  MarketingMetricLike,
  MetricCardDeltaTone,
  MetricCardProps,
  MetricCardTrend,
  ReportsKpiLike,
} from './MetricCard.types'

export function dashboardFooterTrendToMetricTrend(
  footerTrend?: DashboardMetricLike['footerTrend'],
): MetricCardTrend | undefined {
  if (footerTrend === 'up') {
    return 'positive'
  }

  if (footerTrend === 'down') {
    return 'negative'
  }

  if (footerTrend === 'neutral') {
    return 'neutral'
  }

  return undefined
}

export function formatMetricCardDelta(
  delta: number | string,
  tone: MetricCardDeltaTone,
): string {
  if (typeof delta === 'string') {
    return delta
  }

  const absolute = Math.abs(delta)
  const sign = tone === 'increase' ? '+' : '-'
  return `${sign}${absolute}%`
}

/** Map Dashboard metric data → MetricCard props. */
export function metricCardFromDashboard(
  data: DashboardMetricLike,
  overrides?: Partial<MetricCardProps>,
): MetricCardProps {
  const trend =
    overrides?.trend ?? dashboardFooterTrendToMetricTrend(data.footerTrend)

  return {
    label: data.leftLabel,
    rightLabel: data.rightLabel,
    value: data.value,
    valueOver: data.valueOver,
    footer: data.footer,
    trend: trend ?? (data.footer != null ? 'muted' : undefined),
    infoTooltip: data.infoTooltip,
    showInfoIcon: data.showInfoIcon,
    showWarningIcon: data.showWarningIcon,
    valueTone: data.valueTone === 'danger' ? 'danger' : 'default',
    className: data.className,
    ...overrides,
  }
}

/** Map Reports KPI data → MetricCard props. */
export function metricCardFromReportsKpi(
  data: ReportsKpiLike,
  overrides?: Partial<MetricCardProps>,
): MetricCardProps {
  const trendFromTone: MetricCardTrend | undefined =
    data.tone === 'positive'
      ? 'positive'
      : data.tone === 'neutral'
        ? 'neutral'
        : data.footer
          ? 'muted'
          : undefined

  return {
    label: data.title,
    value: data.value,
    unitInline: data.unitInline,
    footer: data.footer,
    trend: trendFromTone,
    valueTone: data.valueTone === 'danger' ? 'danger' : 'default',
    ...overrides,
  }
}

/** Map Marketing Campaign Overview metric → MetricCard props. */
export function metricCardFromMarketing(
  data: MarketingMetricLike,
  overrides?: Partial<MetricCardProps>,
): MetricCardProps {
  const deltaTone: MetricCardDeltaTone | undefined =
    data.status === 'increase' || data.status === 'decrease'
      ? data.status
      : undefined
  const showDelta = deltaTone != null
  const valueSuffix = data.valueAsPercent ? '%' : ''
  const previousSuffix = data.valueAsPercent ? '%' : ''

  return {
    label: data.label,
    value: `${data.value}${valueSuffix}`,
    size: 'lg',
    showInfoIcon: data.infoTooltip != null,
    infoTooltip: data.infoTooltip,
    delta: showDelta ? data.percentage : undefined,
    deltaTone,
    footer:
      showDelta && data.previous != null
        ? `Previously ${data.previous}${previousSuffix}`
        : undefined,
    trend: showDelta ? 'muted' : undefined,
    showFooter: showDelta && data.previous != null,
    ...overrides,
  }
}
